import React, { useState, useRef, useEffect } from 'react';
import {
  Card, CardContent, Typography, Box, Avatar, Select, MenuItem,
  TextField, ClickAwayListener, Collapse,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { TagComponent } from '../tags';
import { statusColors, laborStatusColors, priorityColors, typeColors, getAvatarColor } from '../../theme/tokens';
import { parseTime, formatTime } from '../../utils/timeUtils';

const PRIORITY_ICONS = {
  low: <KeyboardArrowDownIcon sx={{ fontSize: 18, color: priorityColors.low }} />,
  medium: <DragHandleIcon sx={{ fontSize: 18, color: priorityColors.medium }} />,
  high: <KeyboardArrowUpIcon sx={{ fontSize: 18, color: priorityColors.high }} />,
  critical: <KeyboardDoubleArrowUpIcon sx={{ fontSize: 18, color: priorityColors.critical }} />,
};

const TYPE_ICONS = {
  bug: <BugReportIcon sx={{ fontSize: 16, color: typeColors.bug }} />,
  story: <BookmarkIcon sx={{ fontSize: 16, color: typeColors.story }} />,
  task: <CheckBoxIcon sx={{ fontSize: 16, color: typeColors.task }} />,
};

const STATUS_CONFIG = {
  todo: { ...statusColors.todo },
  in_progress: { ...statusColors.in_progress },
  in_review: { ...statusColors.in_review },
  done: { ...statusColors.done },
};

const LABOR_STATUS_CONFIG = {
  hold: { ...laborStatusColors.hold },
  pending: { ...laborStatusColors.pending },
  in_progress: { ...laborStatusColors.in_progress },
  complete: { ...laborStatusColors.complete },
};

// Minimal hidden-icon select styling
const hiddenSelectSx = {
  minWidth: 0,
  '& .MuiSelect-select': { py: 0, px: 0, minHeight: 'unset', display: 'flex', alignItems: 'center' },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-icon': { display: 'none' },
};

const menuPaperSx = { mt: 0.5, borderRadius: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' };

const InlineEdit = ({ value, onChange, sx }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value && draft.trim()) onChange(draft.trim());
    else setDraft(value || '');
  };

  if (!editing) {
    return (
      <Typography
        variant="body2"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        sx={{
          fontWeight: 500, color: 'text.primary', cursor: 'text',
          borderRadius: 0.5, px: 0.25,
          '&:hover': { bgcolor: 'action.hover' },
          ...sx,
        }}
      >
        {value || 'Untitled'}
      </Typography>
    );
  }

  return (
    <ClickAwayListener onClickAway={commit}>
      <TextField
        inputRef={inputRef}
        size="small"
        variant="standard"
        multiline
        maxRows={3}
        value={draft}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { commit(); e.preventDefault(); }
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        inputProps={{ style: { fontSize: 14, fontWeight: 500, padding: 0 } }}
        sx={{ width: '100%' }}
      />
    </ClickAwayListener>
  );
};

const TimeInput = ({ value, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const minutes = parseTime(draft);
    if (minutes !== null && minutes !== value) onChange(minutes);
    setDraft('');
  };

  if (!editing) {
    return (
      <Typography
        variant="caption"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        sx={{
          color: value ? 'text.secondary' : 'text.disabled',
          fontSize: 12, fontWeight: 600, cursor: 'text',
          px: 0.5, py: 0.25, borderRadius: 0.5, whiteSpace: 'nowrap',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {formatTime(value)}
      </Typography>
    );
  }

  return (
    <ClickAwayListener onClickAway={commit}>
      <TextField
        inputRef={inputRef}
        size="small"
        variant="standard"
        value={draft}
        placeholder="e.g. 1h 30m"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { commit(); e.preventDefault(); }
          if (e.key === 'Escape') { setDraft(''); setEditing(false); }
        }}
        inputProps={{ style: { fontSize: 12, width: 70, padding: '2px 4px' } }}
      />
    </ClickAwayListener>
  );
};

// Avatar select for lead and work item users
const AvatarSelect = ({ value, members, onChange, size = 28, allowClear = false }) => (
  <Select
    value={value || ''}
    size="small"
    displayEmpty
    onClick={(e) => e.stopPropagation()}
    onChange={(e) => {
      e.stopPropagation();
      onChange(e.target.value || null);
    }}
    sx={{ height: size, ...hiddenSelectSx }}
    renderValue={(val) => {
      const member = (members || []).find((m) => m._id === val);
      return (
        <Avatar sx={{
          width: size, height: size,
          fontSize: size < 28 ? 10 : 12,
          fontWeight: 600,
          bgcolor: member ? getAvatarColor(member._id) : 'text.disabled',
          cursor: 'pointer',
        }}>
          {member?.name?.[0]?.toUpperCase() || '?'}
        </Avatar>
      );
    }}
    MenuProps={{
      PaperProps: { sx: menuPaperSx },
      onClick: (e) => e.stopPropagation(),
    }}
  >
    {allowClear && <MenuItem value="" sx={{ fontSize: 12, color: 'text.disabled' }}><em>No Lead</em></MenuItem>}
    {(members || []).map((m) => (
      <MenuItem key={m._id} value={m._id} sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
        <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: getAvatarColor(m._id) }}>{m.name?.[0]?.toUpperCase()}</Avatar>
        {m.name}
      </MenuItem>
    ))}
  </Select>
);

export const TaskCard = ({
  task, isDragging, dragHandleProps, workItems, unresolvedComments,
  onStatusChange, onPriorityChange, onTitleChange,
  onWorkItemStatusChange, onWorkItemFieldChange, onWorkItemUserChange,
  members, onTagsChange, projectId, onClick,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Unique work item users for people icons
  const wiUsers = [];
  const seenIds = new Set();
  (workItems || []).forEach((wi) => {
    if (wi.user?._id && !seenIds.has(wi.user._id)) {
      seenIds.add(wi.user._id);
      wiUsers.push(wi.user);
    }
  });

  return (
    <Card
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 2 },
        ...(isDragging && { boxShadow: 4, transform: 'rotate(1.5deg)' }),
        ...(unresolvedComments > 0 && { borderLeft: '3px solid', borderLeftColor: 'error.main' }),
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>

        {/* === ROW 1: Type icon + Task number + spacer + Priority + Status + Lead === */}
        <Box {...dragHandleProps} onClick={onClick} sx={{ cursor: 'pointer' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
            {TYPE_ICONS[task.type] || TYPE_ICONS.task}
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontSize: 12, ml: 0.5 }}>
              {task.taskNumber || ''}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={task.priority || 'medium'}
                size="small"
                onChange={(e) => { if (onPriorityChange) onPriorityChange(e.target.value); }}
                sx={{ height: 24, ...hiddenSelectSx }}
                renderValue={(val) => PRIORITY_ICONS[val] || PRIORITY_ICONS.medium}
                MenuProps={{ PaperProps: { sx: menuPaperSx } }}
              >
                {Object.entries(PRIORITY_ICONS).map(([value, icon]) => (
                  <MenuItem key={value} value={value} sx={{ py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {icon}
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{value}</Typography>
                  </MenuItem>
                ))}
              </Select>
              <Select
                value={task.status || 'todo'}
                size="small"
                onChange={(e) => { if (onStatusChange) onStatusChange(e.target.value); }}
                sx={{
                  height: 26, fontSize: 11, fontWeight: 700, borderRadius: 1,
                  bgcolor: STATUS_CONFIG[task.status]?.bg || statusColors.todo.bg,
                  color: STATUS_CONFIG[task.status]?.color || statusColors.todo.color,
                  '& .MuiSelect-select': { py: 0, px: 1.25, minHeight: 'unset' },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
                MenuProps={{ PaperProps: { sx: menuPaperSx } }}
              >
                {Object.entries(STATUS_CONFIG).map(([value, cfg]) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: 12, fontWeight: 600, color: cfg.color, py: 0.5 }}>
                    {cfg.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>

        {/* === ROW 2: Title (editable, outside drag handle) === */}
        <InlineEdit
          value={task.title}
          onChange={(val) => { if (onTitleChange) onTitleChange(val); }}
          sx={{
            mb: 0.75,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}
        />

        {/* === ROW 3: Tag + Attachments + People avatars === */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box onClick={(e) => e.stopPropagation()}>
            <TagComponent
              taskId={task._id}
              taskTags={task.tags || []}
              projectId={projectId || task.project?._id}
              onTagsChange={onTagsChange}
            />
          </Box>

          {task.attachments && task.attachments.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <AttachFileIcon sx={{ fontSize: 13, color: 'text.disabled', transform: 'rotate(45deg)' }} />
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, fontWeight: 600 }}>
                {task.attachments.length}
              </Typography>
            </Box>
          )}

          {unresolvedComments > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 13, color: 'error.main' }} />
              <Typography variant="caption" sx={{ color: 'error.main', fontSize: 10, fontWeight: 700 }}>
                {unresolvedComments}
              </Typography>
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          {/* People avatars — click to expand/collapse work items */}
          {wiUsers.length > 0 && (
            <Box
              onClick={(e) => { e.stopPropagation(); setExpanded((prev) => !prev); }}
              sx={{ display: 'flex', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              title={expanded ? 'Hide work items' : 'Show work items'}
            >
              {wiUsers.slice(0, 4).map((u, i) => (
                <Avatar key={u._id} sx={{
                  width: 22, height: 22, fontSize: 9, fontWeight: 600,
                  bgcolor: getAvatarColor(u._id),
                  border: '2px solid', borderColor: 'background.paper',
                  ml: i > 0 ? -0.75 : 0,
                }}>
                  {u.name?.[0]?.toUpperCase()}
                </Avatar>
              ))}
              {wiUsers.length > 4 && (
                <Avatar sx={{ width: 22, height: 22, fontSize: 9, bgcolor: 'text.disabled', border: '2px solid', borderColor: 'background.paper', ml: -0.75 }}>
                  +{wiUsers.length - 4}
                </Avatar>
              )}
            </Box>
          )}
        </Box>

        {/* === WORK ITEMS (collapsible) === */}
        {workItems && workItems.length > 0 && (
          <Collapse in={expanded}>
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            {workItems.map((wi) => {
              const statusCfg = LABOR_STATUS_CONFIG[wi.status] || LABOR_STATUS_CONFIG.pending;
              return (
                <Box
                  key={wi._id}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    px: 0.5,
                    mx: -0.5,
                    borderRadius: 1,
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:not(:last-child)': { mb: 0.25 },
                    ...(wi.status === 'hold' && { opacity: 0.5 }),
                  }}
                >
                  {/* User avatar (clickable to change person) */}
                  <AvatarSelect
                    value={wi.user?._id}
                    members={members}
                    onChange={(val) => { if (val && onWorkItemUserChange) onWorkItemUserChange(wi._id, val); }}
                    size={26}
                  />

                  {/* Name */}
                  <Typography variant="body2" sx={{
                    color: 'text.primary', fontSize: 13, fontWeight: 500,
                    flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {wi.user?.name || 'Unknown'}
                  </Typography>

                  {/* Hours */}
                  <TimeInput
                    value={wi.time}
                    onChange={(val) => { if (onWorkItemFieldChange) onWorkItemFieldChange(wi._id, 'time', val); }}
                  />

                  {/* Status */}
                  <Select
                    value={wi.status || 'pending'}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (onWorkItemStatusChange) onWorkItemStatusChange(wi._id, e.target.value);
                    }}
                    sx={{
                      height: 26, fontSize: 11, fontWeight: 600, borderRadius: 1, flexShrink: 0,
                      bgcolor: statusCfg.bg, color: statusCfg.color,
                      '& .MuiSelect-select': { py: 0, px: 1, minHeight: 'unset' },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid', borderColor: statusCfg.color },
                      '& .MuiSelect-icon': { fontSize: 16, right: 0 },
                    }}
                    MenuProps={{ PaperProps: { sx: menuPaperSx }, onClick: (e) => e.stopPropagation() }}
                  >
                    {Object.entries(LABOR_STATUS_CONFIG).map(([value, cfg]) => (
                      <MenuItem key={value} value={value} sx={{ fontSize: 12, fontWeight: 600, color: cfg.color, py: 0.5 }}>
                        {cfg.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              );
            })}
          </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
};
